import type { SocialLink } from "./socialLinks";

/*
 * `showPhoto` is deliberately still here.
 *
 * The photo permission was removed: an uploaded photo is shown, and a member who
 * does not want one shown removes it. The key is retained so the stored columns,
 * the write paths and every historical row stay valid — but nothing reads it any
 * more, and no form offers it. See server/directoryPrivacy.ts, where the photo is
 * assigned without a gate.
 */
export const DIRECTORY_FIELD_KEYS = [
  "showTitle",
  "showCompany",
  "showIndustry",
  "showInterests",
  "showBio",
  "showPhoto",
  "showLocation",
  "showLinkedIn",
  "showEmail",
  "showPhone",
] as const;

export type DirectoryFieldPreferenceKey = (typeof DIRECTORY_FIELD_KEYS)[number];

export type DirectoryPrivacyPreferences = {
  directoryVisible: boolean;
} & Record<DirectoryFieldPreferenceKey, boolean>;

/**
 * What the account-creation form starts every field at.
 *
 * Every field, contact details included, begins enabled: the directory is more
 * useful when members can reach each other, and existing members were opted into
 * the same set, so a new signup is not left more exposed or less findable than
 * someone who joined earlier.
 *
 * These are starting positions, not settings. The create-account form renders
 * DirectoryPrivacyControls over this object and submits whatever the member
 * leaves it as, so anyone who does not want their email or phone listed turns it
 * off before the account exists — the value is never written behind their back.
 */
export const NEW_ACCOUNT_DIRECTORY_PRIVACY = Object.freeze({
  directoryVisible: true,
  showTitle: true,
  showCompany: true,
  showIndustry: true,
  showInterests: true,
  showBio: true,
  showPhoto: true,
  showLocation: true,
  showLinkedIn: true,
  showEmail: true,
  showPhone: true,
}) satisfies DirectoryPrivacyPreferences;

/**
 * What an account created *for* somebody starts at — an administrator adding
 * event attendees in bulk, rather than a person filling in the signup form.
 *
 * The inverse of `NEW_ACCOUNT_DIRECTORY_PRIVACY`, and for the reason that makes
 * that one safe: a member choosing their own settings has seen the controls and
 * left them as they are. Someone entered from an event registration list has
 * seen nothing, so publishing their email and phone to the paid directory would
 * be a disclosure they never agreed to. They are listed once they say so.
 */
export const ADMIN_ADDED_ACCOUNT_DIRECTORY_PRIVACY = Object.freeze({
  directoryVisible: false,
  showTitle: false,
  showCompany: false,
  showIndustry: false,
  showInterests: false,
  showBio: false,
  showPhoto: false,
  showLocation: false,
  showLinkedIn: false,
  showEmail: false,
  showPhone: false,
}) satisfies DirectoryPrivacyPreferences;

export function legacyDirectoryPrivacyBackfill(
  masterVisible: boolean,
): DirectoryPrivacyPreferences {
  return {
    directoryVisible: masterVisible,
    showTitle: masterVisible,
    showCompany: masterVisible,
    showIndustry: masterVisible,
    showInterests: masterVisible,
    showBio: false,
    // A legacy member never consented to a photo, and has none to show.
    showPhoto: false,
    showLocation: false,
    showLinkedIn: false,
    showEmail: false,
    showPhone: false,
  };
}

/**
 * What the person doing the looking is entitled to see.
 *
 * `basic` was called `name_only` when it returned nothing but a name. It now
 * carries the photo and bio a member chose to publish, so the professional
 * details paying members are actually buying — title, company, industry,
 * LinkedIn, email, phone — are what `paid` adds.
 */
export type DirectoryViewerAccess = "paid" | "basic";

export type DirectoryEntry = {
  id: string;
  name: string;
  isPaidMember?: boolean;
  /** True only when the member is visible and explicitly accepts member messages. */
  canMessage?: boolean;
  photoUrl?: string | null;
  /** Original member-owned image retained as a browser error fallback. */
  memberPhotoUrl?: string | null;
  bio?: string | null;
  title?: string | null;
  company?: string | null;
  industry?: string | null;
  interests?: string[] | null;
  location?: string | null;
  linkedinUrl?: string | null;
  /** Present only when the member entered them; see server/directoryPrivacy.ts. */
  socialLinks?: SocialLink[];
  companyWebsiteUrl?: string | null;
  email?: string | null;
  phone?: string | null;
};

export type DirectoryResponse = {
  viewerAccess: DirectoryViewerAccess;
  members: DirectoryEntry[];
};

export type DirectoryProfilePreview = {
  name: string;
  title?: string;
  company?: string;
  industry?: string;
  interests?: string[];
  bio?: string;
  photoUrl?: string | null;
  location?: string;
  linkedinUrl?: string;
  email?: string;
  phone?: string;
};

export type DirectoryPrivacySettings = {
  preferences: DirectoryPrivacyPreferences;
  preview: DirectoryProfilePreview;
};
